import { API_ENDPOINTS } from "../shared/constants";
import type { OrgRole } from "../types/auth";
import apiService from "../shared/apiService";

export interface Organization {
  id: number;
  owner_user_id: number;
  name: string;
  logo_url: string | null;
  cnpj: string | null;
  segment: string | null;
  target_audience: string | null;
  brand_voice?: string | null;
  primary_color?: string | null;
  background_color?: string | null;
  font_family?: string | null;
  phone: string | null;
  website_url: string | null;
  privacy_policy_url: string | null;
  address?: string | null;
  address_number?: string | null;
  province?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  leadflow_company_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientCompany {
  id: number;
  organization_id: number;
  name: string;
  logo_url: string | null;
  cnpj: string | null;
  segment: string | null;
  target_audience: string | null;
  brand_voice?: string | null;
  primary_color?: string | null;
  background_color?: string | null;
  font_family?: string | null;
  phone: string | null;
  website_url: string | null;
  privacy_policy_url: string | null;
  billing_context?: "ORGANIZATION" | "CLIENT";
  address_cep?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_neighborhood?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  leadflow_company_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientsListResponse {
  success: boolean;
  clients: ClientCompany[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface WhatsappCrmSubscription {
  id: number;
  /** Dono da assinatura: a própria organização OU uma Empresa Cliente (exatamente um). */
  organization_id: number | null;
  client_company_id: number | null;
  asaas_customer_id: string | null;
  asaas_subscription_id: string | null;
  status: "pending" | "active" | "overdue" | "canceled";
  base_price_cents: number;
  included_seats: number;
  extra_seat_price_cents: number;
  last_known_seat_count: number;
  current_value_cents: number;
  next_due_date: string | null;
  created_at: string | null;
  updated_at: string | null;
  canceled_at: string | null;
}

export interface WhatsappCrmSubscriptionResponse {
  success: boolean;
  subscription: WhatsappCrmSubscription | null;
  checkout_url?: string;
  error?: string;
}

export interface WhatsappCrmBilledSeat {
  name: string | null;
  email: string;
}

/** Quem está sendo cobrado como assento. `source`: "crm" = equipe informada pelo WhatsApp CRM;
 * "organization" = regra antiga (todos os usuários ativos), enquanto o CRM não sincronizou. */
export interface WhatsappCrmSeatsResponse {
  success: boolean;
  source: "crm" | "organization" | null;
  seat_count?: number;
  seats: WhatsappCrmBilledSeat[];
  synced_at?: string | null;
  error?: string;
}

export interface BrandKitSuggestion {
  primary_color: string | null;
  background_color: string | null;
  font_family: string | null;
  source: string | null;
  confidence: "high" | "low";
  detected_at: string;
  status: "pending";
}

export interface BrandKitSuggestionResponse {
  success: boolean;
  suggestion: BrandKitSuggestion | null;
}

export interface OrganizationResponse {
  success: boolean;
  organization: Organization | null;
}

export interface ClientResponse {
  success: boolean;
  client: ClientCompany;
}

export interface OrganizationUser {
  id: number;
  name: string;
  email: string;
  org_role: OrgRole;
  is_active: boolean;
  created_at: string | null;
}

export interface OrganizationResponseWithRole extends OrganizationResponse {
  current_user_org_role?: OrgRole;
}

export interface OrganizationUsersResponse {
  success: boolean;
  users: OrganizationUser[];
}

class OrganizationService {
  async getOrganization(): Promise<OrganizationResponse> {
    return await apiService.get<OrganizationResponse>(
      API_ENDPOINTS.ORGANIZATION.GET,
    );
  }

  async createOrganization(data: {
    name: string;
    logo_url?: string;
    cnpj?: string;
    segment?: string;
    target_audience?: string;
    phone?: string;
    website_url?: string;
    privacy_policy_url?: string;
    address?: string;
    address_number?: string;
    province?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  }): Promise<OrganizationResponse> {
    return await apiService.post<OrganizationResponse>(
      API_ENDPOINTS.ORGANIZATION.CREATE,
      data,
    );
  }

  async updateOrganization(data: {
    name?: string;
    logo_url?: string;
    cnpj?: string;
    segment?: string;
    target_audience?: string;
    brand_voice?: string;
    primary_color?: string;
    background_color?: string;
    font_family?: string;
    phone?: string;
    website_url?: string;
    privacy_policy_url?: string;
    address?: string;
    address_number?: string;
    province?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    leadflow_company_id?: string;
  }): Promise<OrganizationResponse> {
    return await apiService.put<OrganizationResponse>(
      API_ENDPOINTS.ORGANIZATION.UPDATE,
      data,
    );
  }

  /** Envia o logotipo da empresa cliente e devolve a URL (gravada depois, no update). */
  async uploadClientLogo(
    clientId: number,
    file: File,
  ): Promise<{ success: boolean; logo_url?: string; error?: string }> {
    const formData = new FormData();
    formData.append("logo", file);
    return await apiService.post<{
      success: boolean;
      logo_url?: string;
      error?: string;
    }>(API_ENDPOINTS.ORGANIZATION.CLIENT_LOGO(clientId), formData);
  }

  /** Envia o logotipo e devolve a URL (gravada depois, no update da organização). */
  async uploadOrganizationLogo(
    file: File,
  ): Promise<{ success: boolean; logo_url?: string; error?: string }> {
    const formData = new FormData();
    formData.append("logo", file);
    return await apiService.post<{
      success: boolean;
      logo_url?: string;
      error?: string;
    }>(API_ENDPOINTS.ORGANIZATION.LOGO, formData);
  }

  async getBrandKitSuggestion(): Promise<BrandKitSuggestionResponse> {
    return await apiService.get<BrandKitSuggestionResponse>(
      API_ENDPOINTS.ORGANIZATION.BRAND_KIT_SUGGESTION,
    );
  }

  async dismissBrandKitSuggestion(): Promise<{ success: boolean }> {
    return await apiService.post<{ success: boolean }>(
      API_ENDPOINTS.ORGANIZATION.BRAND_KIT_SUGGESTION_DISMISS,
      {},
    );
  }

  async getClientBrandKitSuggestion(
    id: number,
  ): Promise<BrandKitSuggestionResponse> {
    return await apiService.get<BrandKitSuggestionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_BRAND_KIT_SUGGESTION(id),
    );
  }

  async dismissClientBrandKitSuggestion(
    id: number,
  ): Promise<{ success: boolean }> {
    return await apiService.post<{ success: boolean }>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_BRAND_KIT_SUGGESTION_DISMISS(id),
      {},
    );
  }

  async listClients(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ClientsListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.search) searchParams.set("search", params.search);
    const query = searchParams.toString();
    const url = query
      ? `${API_ENDPOINTS.ORGANIZATION.CLIENTS}?${query}`
      : API_ENDPOINTS.ORGANIZATION.CLIENTS;
    return await apiService.get<ClientsListResponse>(url);
  }

  /** Lista todas as empresas cliente (sem paginação), para dropdowns e modal de permissões do editor */
  async listClientOptions(): Promise<{
    success: boolean;
    clients: ClientCompany[];
  }> {
    return await apiService.get<{ success: boolean; clients: ClientCompany[] }>(
      API_ENDPOINTS.ORGANIZATION.CLIENTS_OPTIONS,
    );
  }

  async getClient(id: number): Promise<ClientResponse> {
    return await apiService.get<ClientResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT(id),
    );
  }

  async createClient(data: {
    name: string;
    logo_url?: string;
    cnpj?: string;
    segment?: string;
    target_audience?: string;
    phone?: string;
    website_url?: string;
    privacy_policy_url?: string;
  }): Promise<ClientResponse> {
    return await apiService.post<ClientResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENTS,
      data,
    );
  }

  async updateClient(
    id: number,
    data: {
      name?: string;
      logo_url?: string;
      cnpj?: string;
      segment?: string;
      target_audience?: string;
      brand_voice?: string;
      primary_color?: string;
      background_color?: string;
      font_family?: string;
      phone?: string;
      website_url?: string;
      privacy_policy_url?: string;
      billing_context?: "ORGANIZATION" | "CLIENT";
      address_cep?: string;
      address_street?: string;
      address_number?: string;
      address_complement?: string;
      address_neighborhood?: string;
      address_city?: string;
      address_state?: string;
      leadflow_company_id?: string;
    },
  ): Promise<ClientResponse> {
    return await apiService.put<ClientResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT(id),
      data,
    );
  }

  /** Status atual da assinatura recorrente do WhatsApp CRM desta Empresa Cliente (null se
   * nunca foi ativada). */
  async getWhatsappCrmSubscription(
    clientId: number,
  ): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.get<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_WHATSAPP_CRM_SUBSCRIPTION(clientId),
    );
  }

  /** Ativa a assinatura recorrente do WhatsApp CRM pra esta Empresa Cliente — conta os
   * assentos ativos (usuários com acesso real a ela) e cria a cobrança no Asaas. */
  async activateWhatsappCrmSubscription(
    clientId: number,
  ): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.post<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_WHATSAPP_CRM_SUBSCRIPTION(clientId),
      {},
    );
  }

  /** Cancela a assinatura do WhatsApp CRM desta Empresa Cliente — cancela no Asaas primeiro, só
   * marca cancelada se isso funcionar. */
  async cancelWhatsappCrmSubscription(
    clientId: number,
  ): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.delete<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_WHATSAPP_CRM_SUBSCRIPTION(clientId),
    );
  }

  /** "Reajustar": reaplica a config global ATUAL do plano numa assinatura já existente — útil
   * quando o preço global mudou depois da ativação e o admin quer atualizar explicitamente. */
  async resyncWhatsappCrmSubscription(
    clientId: number,
  ): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.post<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_WHATSAPP_CRM_SUBSCRIPTION_RESYNC(
        clientId,
      ),
      {},
    );
  }

  /** "Preço personalizado": define base/assentos inclusos/assento extra PRÓPRIOS pra esta
   * assinatura (ex.: cliente promocional) — diferente da config global, sobrevive a
   * cancelamento/reativação. Depois disso, não use "Reajustar" nesta assinatura (ele reaplica a
   * config global de propósito e sobrescreveria o preço personalizado). */
  async setWhatsappCrmSubscriptionPrice(
    clientId: number,
    data: {
      base_price_cents: number;
      included_seats: number;
      extra_seat_price_cents: number;
    },
  ): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.put<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.CLIENT_WHATSAPP_CRM_SUBSCRIPTION_PRICE(
        clientId,
      ),
      data,
    );
  }

  /** Status da assinatura do WhatsApp CRM da própria organização (null se nunca ativada). */
  async getWhatsappCrmSubscriptionForOrganization(): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.get<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SUBSCRIPTION,
    );
  }

  /** Quem está sendo cobrado como assento na assinatura do WhatsApp CRM da própria organização. */
  async getWhatsappCrmSeatsForOrganization(): Promise<WhatsappCrmSeatsResponse> {
    return await apiService.get<WhatsappCrmSeatsResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SEATS,
    );
  }

  /** Ativa a assinatura do WhatsApp CRM da própria organização (cobra por usuário ativo dela). */
  async activateWhatsappCrmSubscriptionForOrganization(): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.post<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SUBSCRIPTION,
      {},
    );
  }

  /** Cancela a assinatura do WhatsApp CRM da própria organização. */
  async cancelWhatsappCrmSubscriptionForOrganization(): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.delete<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SUBSCRIPTION,
    );
  }

  /** "Reajustar" no nível da organização. */
  async resyncWhatsappCrmSubscriptionForOrganization(): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.post<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SUBSCRIPTION_RESYNC,
      {},
    );
  }

  /** "Preço personalizado" no nível da organização. */
  async setWhatsappCrmSubscriptionPriceForOrganization(data: {
    base_price_cents: number;
    included_seats: number;
    extra_seat_price_cents: number;
  }): Promise<WhatsappCrmSubscriptionResponse> {
    return await apiService.put<WhatsappCrmSubscriptionResponse>(
      API_ENDPOINTS.ORGANIZATION.WHATSAPP_CRM_SUBSCRIPTION_PRICE,
      data,
    );
  }

  async deleteClient(id: number): Promise<{ success: boolean }> {
    return await apiService.delete<{ success: boolean }>(
      API_ENDPOINTS.ORGANIZATION.CLIENT(id),
    );
  }

  async listUsers(): Promise<OrganizationUsersResponse> {
    return await apiService.get<OrganizationUsersResponse>(
      API_ENDPOINTS.ORGANIZATION.USERS,
    );
  }

  async createUser(data: {
    email: string;
    name: string;
    password: string;
    role?: OrgRole;
  }): Promise<{ success: boolean; user: OrganizationUser }> {
    return await apiService.post<{ success: boolean; user: OrganizationUser }>(
      API_ENDPOINTS.ORGANIZATION.USERS,
      data,
    );
  }

  async updateUser(
    id: number,
    data: { name?: string; email?: string },
  ): Promise<{ success: boolean; user: OrganizationUser }> {
    return await apiService.put<{ success: boolean; user: OrganizationUser }>(
      API_ENDPOINTS.ORGANIZATION.USER(id),
      data,
    );
  }

  async changeUserRole(
    id: number,
    role: OrgRole,
  ): Promise<{ success: boolean; user: OrganizationUser }> {
    return await apiService.patch<{ success: boolean; user: OrganizationUser }>(
      API_ENDPOINTS.ORGANIZATION.USER_ROLE(id),
      { role },
    );
  }

  async deleteUser(id: number): Promise<{ success: boolean }> {
    return await apiService.delete<{ success: boolean }>(
      API_ENDPOINTS.ORGANIZATION.USER(id),
    );
  }

  /** Opções para o dropdown de contexto (Organização + Empresas Cliente permitidas) */
  async getContextOptions(organizationId?: number): Promise<{
    success: boolean;
    organization: Organization | null;
    client_companies: ClientCompany[];
    available_organizations?: Organization[];
    is_system_admin?: boolean;
  }> {
    const url = organizationId
      ? `${API_ENDPOINTS.ORGANIZATION.CONTEXT_OPTIONS}?organization_id=${organizationId}`
      : API_ENDPOINTS.ORGANIZATION.CONTEXT_OPTIONS;
    return await apiService.get(url);
  }

  /** Listar empresas cliente às quais um usuário (EDITOR) tem acesso */
  async getUserClientAccess(
    userId: number,
  ): Promise<{ success: boolean; client_company_ids: number[] }> {
    return await apiService.get(
      API_ENDPOINTS.ORGANIZATION.USER_CLIENT_ACCESS(userId),
    );
  }

  /** Definir empresas cliente às quais um usuário (EDITOR) tem acesso */
  async setUserClientAccess(
    userId: number,
    clientCompanyIds: number[],
  ): Promise<{ success: boolean; client_company_ids: number[] }> {
    return await apiService.put(
      API_ENDPOINTS.ORGANIZATION.USER_CLIENT_ACCESS(userId),
      { client_company_ids: clientCompanyIds },
    );
  }

  /** Configurações sociais da organização (credenciais por plataforma) */
  async getOrganizationSocialConfigs(): Promise<{
    success: boolean;
    configs: Record<string, any>;
  }> {
    return await apiService.get(API_ENDPOINTS.ORGANIZATION.SOCIAL_CONFIG);
  }

  async saveOrganizationSocialConfig(
    platform: string,
    data: Record<string, any>,
  ): Promise<{ success: boolean; config?: any }> {
    return await apiService.post(
      API_ENDPOINTS.ORGANIZATION.SOCIAL_CONFIG_PLATFORM(platform),
      data,
    );
  }

  async verifyOrganizationSocialConfig(
    platform: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return await apiService.post(
      API_ENDPOINTS.ORGANIZATION.SOCIAL_CONFIG_VERIFY(platform),
      {},
    );
  }

  /** Configurações sociais de uma empresa cliente */
  async getClientSocialConfigs(
    clientId: number,
  ): Promise<{ success: boolean; configs: Record<string, any> }> {
    return await apiService.get(
      API_ENDPOINTS.ORGANIZATION.CLIENT_SOCIAL_CONFIG(clientId),
    );
  }

  async saveClientSocialConfig(
    clientId: number,
    platform: string,
    data: Record<string, any>,
  ): Promise<{ success: boolean; config?: any }> {
    return await apiService.post(
      API_ENDPOINTS.ORGANIZATION.CLIENT_SOCIAL_CONFIG_PLATFORM(
        clientId,
        platform,
      ),
      data,
    );
  }

  async verifyClientSocialConfig(
    clientId: number,
    platform: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return await apiService.post(
      API_ENDPOINTS.ORGANIZATION.CLIENT_SOCIAL_CONFIG_VERIFY(
        clientId,
        platform,
      ),
      {},
    );
  }

  async listGoogleBusinessAccounts(
    clientId?: number,
  ): Promise<{ success: boolean; accounts: any[]; error?: string }> {
    const url = clientId
      ? API_ENDPOINTS.ORGANIZATION.CLIENT_GOOGLE_DISCOVERY_ACCOUNTS(clientId)
      : API_ENDPOINTS.ORGANIZATION.GOOGLE_DISCOVERY_ACCOUNTS;
    return await apiService.get<{
      success: boolean;
      accounts: any[];
      error?: string;
    }>(url);
  }

  async listGoogleBusinessLocations(
    accountName: string,
    clientId?: number,
  ): Promise<{ success: boolean; locations: any[]; error?: string }> {
    const url = clientId
      ? API_ENDPOINTS.ORGANIZATION.CLIENT_GOOGLE_DISCOVERY_LOCATIONS(
          clientId,
          accountName,
        )
      : API_ENDPOINTS.ORGANIZATION.GOOGLE_DISCOVERY_LOCATIONS(accountName);
    return await apiService.get<{
      success: boolean;
      locations: any[];
      error?: string;
    }>(url);
  }

  async getGoogleStatus(
    companyId: number,
    isClient: boolean = false,
  ): Promise<{
    connected: boolean;
    status?: string;
    google_account_email?: string;
    accounts_count?: number;
    locations_count?: number;
    accounts?: any[];
    locations?: any[];
  }> {
    const type = isClient ? "client" : "org";
    const url = `${API_ENDPOINTS.ORGANIZATION.GOOGLE_BUSINESS.STATUS(companyId)}?type=${type}`;
    return await apiService.get<any>(url);
  }

  async getGoogleConnectUrl(
    companyId: number,
    isClient: boolean = false,
  ): Promise<{ success: boolean; auth_url: string }> {
    const type = isClient ? "client" : "org";
    const url = `${API_ENDPOINTS.ORGANIZATION.GOOGLE_BUSINESS.CONNECT(companyId)}&json=true&type=${type}`;
    return await apiService.get<any>(url);
  }

  async syncGoogle(
    companyId: number,
    isClient: boolean = false,
  ): Promise<{ success: boolean }> {
    const type = isClient ? "client" : "org";
    const url = API_ENDPOINTS.ORGANIZATION.GOOGLE_BUSINESS.SYNC;
    return await apiService.post<{ success: boolean }>(url, {
      company_id: companyId,
      type: type,
    });
  }

  async disconnectGoogle(
    companyId: number,
    isClient: boolean = false,
  ): Promise<{ success: boolean }> {
    const type = isClient ? "client" : "org";
    const url = API_ENDPOINTS.ORGANIZATION.GOOGLE_BUSINESS.DISCONNECT;
    return await apiService.post<{ success: boolean }>(url, {
      company_id: companyId,
      type: type,
    });
  }
}

export const organizationService = new OrganizationService();
export default organizationService;
